
import React from 'react';
import { TrendingUp, TrendingDown, Users, PieChart, BarChart4 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { BotPremiumDetails } from '@/types/premium-bots';
import { Skeleton } from '@/components/ui/skeleton';
import { formatNumber, formatPercentage } from '@/components/premium-bots/utils/formatting';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAnimatedCounter } from '@/hooks/use-animated-counter';

interface EnhancedBotStatsSectionProps {
  premiumDetails: BotPremiumDetails | undefined;
  isLoading: boolean;
}

const StatCard = ({ 
  title, 
  value, 
  icon: Icon, 
  color, 
  progress, 
  isHighlight = false,
  isLoading = false,
  isMobile = false 
}: {
  title: string;
  value: string | number;
  icon: any;
  color: string;
  progress: number;
  isHighlight?: boolean;
  isLoading?: boolean;
  isMobile?: boolean;
}) => {
  const numericValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) || 0 : value;
  const { current } = useAnimatedCounter({ 
    target: numericValue, 
    duration: 1500, 
    enabled: !isLoading,
    delay: Math.random() * 300 
  });

  const getColorClasses = (color: string) => {
    const colors = {
      amber: {
        bg: 'bg-amber-500/10',
        border: 'border-amber-500/30',
        text: 'text-amber-500',
        progress: 'bg-amber-500',
        shadow: 'shadow-amber-500/20',
      },
      blue: {
        bg: 'bg-blue-500/10',
        border: 'border-blue-500/30',
        text: 'text-blue-500',
        progress: 'bg-blue-500',
        shadow: 'shadow-blue-500/20',
      },
      green: {
        bg: 'bg-green-500/10',
        border: 'border-green-500/30',
        text: 'text-green-500',
        progress: 'bg-green-500',
        shadow: 'shadow-green-500/20',
      },
      red: {
        bg: 'bg-red-500/10',
        border: 'border-red-500/30',
        text: 'text-red-500',
        progress: 'bg-red-500',
        shadow: 'shadow-red-500/20',
      },
      purple: {
        bg: 'bg-purple-500/10',
        border: 'border-purple-500/30',
        text: 'text-purple-500',
        progress: 'bg-purple-500',
        shadow: 'shadow-purple-500/20',
      },
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  const colorClasses = getColorClasses(color);

  return (
    <Card className={`group relative overflow-hidden transition-all duration-500 hover:scale-105 cursor-pointer ${
      isHighlight ? `border-2 ${colorClasses.border} ${colorClasses.shadow} shadow-lg` : 'border hover:border-border/60'
    } ${colorClasses.bg}`}>
      {/* Animated background */}
      <div className={`absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
      
      {/* Header */}
      <div className={`${colorClasses.bg} p-2 text-center border-b ${colorClasses.border}`}>
        <span className={`${isMobile ? "text-xs" : "text-sm"} font-medium ${colorClasses.text} uppercase tracking-wider`}>
          {title}
        </span>
      </div>
      
      <CardContent className={`relative ${isMobile ? "p-3" : "p-4"} flex flex-col items-center justify-center space-y-3`}>
        {/* Icon and Value */}
        <div className="flex items-center justify-center space-x-2">
          <div className={`${isMobile ? "p-2" : "p-2.5"} rounded-lg ${colorClasses.bg} transition-transform duration-300 group-hover:scale-110`}>
            <Icon className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} ${colorClasses.text}`} />
          </div>
          <span className={`${isMobile ? "text-lg" : "text-2xl"} font-bold transition-all duration-300 ${
            isHighlight ? colorClasses.text : 'text-foreground'
          }`}>
            {typeof value === 'string' ? 
              value.replace(/[0-9.-]/g, '') + current.toFixed(value.includes('%') ? 1 : 0) :
              current.toFixed(0)
            }
          </span>
        </div>
        
        {/* Circular Progress */}
        <div className="w-full space-y-2">
          <div className="relative">
            <Progress 
              value={progress} 
              className={`h-2 ${colorClasses.bg}`}
              style={{
                transition: 'all 1.5s cubic-bezier(0.4, 0, 0.2, 1)',
                transitionDelay: '200ms',
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>0</span>
            <span>{Math.round(progress)}%</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const EnhancedBotStatsSection = ({ 
  premiumDetails, 
  isLoading 
}: EnhancedBotStatsSectionProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-36 w-full" />
        ))}
      </div>
    );
  }

  // Helper function to safely parse numeric values
  const parseNumericValue = (value: string | number | undefined, fallback: number = 0): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value.replace(/[^0-9.-]/g, ''));
      return isNaN(parsed) ? fallback : parsed;
    }
    return fallback;
  };

  const stats = [
    {
      title: t('win_rate'),
      value: premiumDetails?.winrate || t('not_available'),
      icon: PieChart,
      color: 'amber',
      progress: parseNumericValue(premiumDetails?.winrate, 0),
    },
    {
      title: t('profit_factor'),
      value: premiumDetails?.profit_factor || t('not_available'),
      icon: BarChart4,
      color: 'blue',
      progress: Math.min(parseNumericValue(premiumDetails?.profit_factor, 0) * 20, 100),
    },
    {
      title: t('potential_profit'),
      value: formatPercentage(premiumDetails?.potential_profit),
      icon: TrendingUp,
      color: 'green',
      progress: parseNumericValue(premiumDetails?.potential_profit, 0),
      isHighlight: true,
    },
    {
      title: t('max_drawdown'),
      value: formatPercentage(premiumDetails?.max_drawdown, true),
      icon: TrendingDown,
      color: 'red',
      progress: parseNumericValue(premiumDetails?.max_drawdown, 0),
      isHighlight: true,
    },
    {
      title: t('active_accounts'),
      value: formatNumber(premiumDetails?.subscribers_total || 0),
      icon: Users,
      color: 'purple',
      progress: Math.min((premiumDetails?.subscribers_total || 0) * 2, 100),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Animated title */}
      <div className="text-center">
        <h2 className={`${isMobile ? "text-base" : "text-lg"} font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent animate-fade-in`}>
          {t('key_statistics')}
        </h2>
        <div className="w-24 h-1 bg-gradient-to-r from-primary to-primary/50 rounded-full mx-auto mt-2" />
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {stats.map((stat, index) => (
          <div 
            key={stat.title}
            className="animate-fade-in"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <StatCard
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              color={stat.color}
              progress={stat.progress}
              isHighlight={stat.isHighlight}
              isLoading={isLoading}
              isMobile={isMobile}
            />
          </div>
        ))}
      </div>
      
      {/* Additional info */}
      {premiumDetails?.connected_accounts_total && (
        <div className="text-center">
          <p className={`${isMobile ? "text-xs" : "text-sm"} text-muted-foreground animate-fade-in`}>
            {formatNumber(premiumDetails.connected_accounts_total)} {t('connected_accounts')} {t('currently_active')}
          </p>
        </div>
      )}
    </div>
  );
};
